import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../firebaseConfig";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";

import {
  FaCalendarAlt,
  FaArrowLeft,
  FaClock,
  FaFacebookF,
  FaWhatsapp,
  FaTelegramPlane,
   FaShareAlt,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

const PhotoDetails = () => {
  const { id } = useParams();

  const [photo, setPhoto] = useState(null);
  const [relatedPhotos, setRelatedPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [hoveredRelated, setHoveredRelated] = useState(null);
  const [hoveredBack, setHoveredBack] = useState(false);
  const [hoveredShare, setHoveredShare] = useState(null);

  // =====================================================
  // LOAD PHOTO
  // =====================================================

  useEffect(() => {
    const fetchPhoto = async () => {
      try {
        setLoading(true);

        const photoRef = doc(db, "photos", id);
        const photoSnap = await getDoc(photoRef);

        if (!photoSnap.exists()) {
          setPhoto(null);
          setLoading(false);
          return;
        }

        const photoData = {
          id: photoSnap.id,
          ...photoSnap.data(),
        };

        setPhoto(photoData);

        // =================================================
        // RELATED PHOTOS
        // =================================================

        if (photoData.category) {
          try {
         const relatedQuery = query(
  collection(db, "photos"),
  where("category", "==", photoData.category),
  orderBy("timestamp", "desc"),
  limit(6)
);

            const relatedSnap = await getDocs(relatedQuery);

            const related = relatedSnap.docs
              .map((item) => ({
                id: item.id,
                ...item.data(),
              }))
              .filter((item) => item.id !== id)
              .slice(0, 5);

            setRelatedPhotos(related);
          } catch (relatedError) {
            console.error(
              "Related photos error:",
              relatedError
            );

            setRelatedPhotos([]);
          }
        }
      } catch (error) {
        console.error(
          "Photo details error:",
          error
        );

        setPhoto(null);
      }

      setLoading(false);
    };

    if (id) {
      fetchPhoto();
    }
  }, [id]);

  // =====================================================
  // DATE
  // =====================================================

  const formatDate = (timestamp) => {
    if (!timestamp) return "";

    try {
      const date = timestamp.seconds
        ? new Date(timestamp.seconds * 1000)
        : new Date(timestamp);

      return date.toLocaleDateString("bn-BD", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  // =====================================================
  // SHARE LINKS
  // =====================================================

  const currentUrl = window.location.href;

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      currentUrl
    )}`,

    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      currentUrl
    )}&text=${encodeURIComponent(
      photo?.title || ""
    )}`,

    whatsapp: `https://wa.me/?text=${encodeURIComponent(
      `${photo?.title || ""} ${currentUrl}`
    )}`,

    telegram: `https://t.me/share/url?url=${encodeURIComponent(
      currentUrl
    )}&text=${encodeURIComponent(
      photo?.title || ""
    )}`,
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "60px 20px",
          textAlign: "center",
          color: "#777",
        }}
      >
        <div
          style={{
            width: "35px",
            height: "35px",
            border: "4px solid #eee",
            borderTop: "4px solid #d2202a",
            borderRadius: "50%",
            margin: "0 auto 15px",
            animation:
              "photoDetailsSpin 1s linear infinite",
          }}
        />

        <div
          style={{
            fontSize: "18px",
            fontWeight: "600",
          }}
        >
          ছবি লোড হচ্ছে...
        </div>

        <style>{`
          @keyframes photoDetailsSpin {
            0% {
              transform: rotate(0deg);
            }

            100% {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    );
  }

  // =====================================================
  // PHOTO NOT FOUND
  // =====================================================

  if (!photo) {
    return (
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "70px 20px",
          textAlign: "center",
        }}
      >
        <h2
          style={{
            marginBottom: "15px",
            color: "#555",
          }}
        >
          ছবিটি পাওয়া যায়নি।
        </h2>

        <Link
          to="/photos"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 18px",
            background: "#d2202a",
            color: "#fff",
            textDecoration: "none",
            borderRadius: "4px",
            fontWeight: "bold",
          }}
        >
          <FaArrowLeft />
          ফটো গ্যালারিতে ফিরে যান
        </Link>
      </div>
    );
  }

  // =====================================================
  // MAIN
  // =====================================================

  return (
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "20px",
      }}
    >
      {/* =================================================
          BACK BUTTON
      ================================================= */}

      <div style={{ marginBottom: "20px" }}>
        <Link
          to="/photos"
          onMouseEnter={() => setHoveredBack(true)}
          onMouseLeave={() => setHoveredBack(false)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 15px",
            background: hoveredBack
              ? "#8b0000"
              : "#d2202a",
            color: "#fff",
            textDecoration: "none",
            borderRadius: "4px",
            fontWeight: "bold",
            transition: "0.2s",
            transform: hoveredBack
              ? "translateY(-2px)"
              : "translateY(0)",
          }}
        >
          <FaArrowLeft />
          ফটো গ্যালারি
        </Link>
      </div>

      {/* =================================================
          CONTENT LAYOUT
      ================================================= */}

      <div
        className="photo-details-layout"
        style={{
          display: "grid",
          gridTemplateColumns:
            relatedPhotos.length > 0
              ? "minmax(0, 1fr) 320px"
              : "1fr",
          gap: "30px",
          alignItems: "start",
        }}
      >
        {/* =================================================
            MAIN PHOTO
        ================================================= */}

        <main>
          {/* CATEGORY */}

          {photo.category && (
            <div
              style={{
                color: "#d2202a",
                fontSize: "14px",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              {photo.category}
            </div>
          )}

          {/* TITLE */}

          <h1
            style={{
              margin: "0 0 12px",
              fontSize: "32px",
              lineHeight: "1.45",
              color: "#222",
              fontWeight: "700",
            }}
          >
            {photo.title}
          </h1>

          {/* DATE */}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              color: "#777",
              fontSize: "14px",
              marginBottom: "18px",
            }}
          >
            <FaCalendarAlt size={13} />
            <span>{formatDate(photo.timestamp)}</span>
          </div>

          {/* SHARE */}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "20px",
            }}
          >
            <span
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                color: "#555",
              }}
            >
              শেয়ার করুন
            </span>

            {[
              {
                key: "facebook",
                href: shareLinks.facebook,
                icon: <FaFacebookF />,
                bg: "#1877F2",
              },
              {
                key: "twitter",
                href: shareLinks.twitter,
                icon: <FaXTwitter />,
                bg: "#000",
              },
              {
                key: "whatsapp",
                href: shareLinks.whatsapp,
                icon: <FaWhatsapp />,
                bg: "#25D366",
              },
              {
                key: "telegram",
                href: shareLinks.telegram,
                icon: <FaTelegramPlane />,
                bg: "#229ED9",
              },
            ].map((item) => (
              <a
                key={item.key}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() =>
                  setHoveredShare(item.key)
                }
                onMouseLeave={() =>
                  setHoveredShare(null)
                }
                style={{
                  width: "34px",
                  height: "34px",
                  borderRadius: "50%",
                  background:
                    hoveredShare === item.key
                      ? "#333"
                      : item.bg,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textDecoration: "none",
                  transition: "all 0.2s ease",
                  transform:
                    hoveredShare === item.key
                      ? "translateY(-3px) scale(1.08)"
                      : "translateY(0) scale(1)",
                  boxShadow:
                    hoveredShare === item.key
                      ? "0 4px 10px rgba(0,0,0,0.2)"
                      : "none",
                }}
              >
                {item.icon}
              </a>
            ))}
          </div>

          {/* =================================================
              LARGE IMAGE
          ================================================= */}

          <div
            style={{
              width: "100%",
              background: "#f5f5f5",
              borderRadius: "6px",
              overflow: "hidden",
            }}
          >
            <img
              src={photo.image || photo.url}
              alt={photo.title || "Photo"}
              style={{
                width: "100%",
                maxHeight: "750px",
                objectFit: "contain",
                display: "block",
                margin: "0 auto",
              }}
            />
          </div>

          {/* CAPTION */}

          {photo.caption && (
            <div
              style={{
                padding: "10px 0",
                color: "#777",
                fontSize: "14px",
                lineHeight: "1.6",
                borderBottom:
                  "1px solid #eee",
              }}
            >
              {photo.caption}
            </div>
          )}

          {/* DESCRIPTION */}

          {photo.description && (
            <div
              style={{
                marginTop: "20px",
                fontSize: "17px",
                lineHeight: "1.8",
                color: "#333",
                whiteSpace: "pre-line",
              }}
            >
              {photo.description}
            </div>
          )}
        </main>

        {/* =================================================
            RELATED PHOTOS
        ================================================= */}

        {relatedPhotos.length > 0 && (
          <aside>
            <div
              style={{
                borderLeft:
                  "5px solid #d2202a",
                paddingLeft: "10px",
                marginBottom: "15px",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "22px",
                  color: "#222",
                }}
              >
                আরও ছবি
              </h2>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
              }}
            >
              {relatedPhotos.map((item) => (
                <Link
                  key={item.id}
                  to={`/photo/${item.id}`}
                  onMouseEnter={() =>
                    setHoveredRelated(item.id)
                  }
                  onMouseLeave={() =>
                    setHoveredRelated(null)
                  }
                  style={{
                    display: "flex",
                    gap: "10px",
                    textDecoration: "none",
                    color: "#222",
                    paddingBottom: "12px",
                    borderBottom:
                      "1px solid #eee",
                    background:
                      hoveredRelated === item.id
                        ? "#f5f8ff"
                        : "transparent",
                    transition: "0.2s",
                  }}
                >
                  <img
                    src={item.image || item.url}
                    alt={item.title || "Photo"}
                    loading="lazy"
                    style={{
                      width: "110px",
                      height: "75px",
                      objectFit: "cover",
                      borderRadius: "4px",
                      flexShrink: 0,
                    }}
                  />

                  <div>
                    <h3
                      style={{
                        margin: "0 0 6px",
                        fontSize: "16px",
                        lineHeight: "1.4",
                        color:
                          hoveredRelated ===
                          item.id
                            ? "#4169E1"
                            : "#222",
                        transition: "0.2s",
                      }}
                    >
                      {item.title}
                    </h3>

                    <div
                      style={{
                        fontSize: "12px",
                        color: "#777",
                      }}
                    >
                      <FaCalendarAlt
                        size={11}
                      />{" "}
                      {formatDate(
                        item.timestamp
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        )}
      </div>

      {/* =================================================
          RESPONSIVE
      ================================================= */}

      <style>{`
        @media (max-width: 900px) {
          .photo-details-layout {
            grid-template-columns: 1fr !important;
          }
        }

        @media (max-width: 600px) {
          .photo-details-layout h1 {
            font-size: 25px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PhotoDetails;